import { useState, useCallback, useRef, useEffect } from 'react';
import config from 'src/config';

// Backend API endpoint
const API_BASE = `${config.apiBaseUrl}/ar-vapi`;

export interface TranscriptMessage {
  id: string;
  role: 'assistant' | 'user';
  text: string;
  timestamp: Date;
  isFinal: boolean;
}

export interface CustomerCallState {
  customerId: string;
  customerName: string;
  status: 'idle' | 'connecting' | 'ringing' | 'active' | 'ended' | 'error';
  transcript: TranscriptMessage[];
  error?: string;
  callId?: string;
}

interface UseARVapiCallReturn {
  customerCalls: Map<string, CustomerCallState>;
  startCall: (customer: {
    id: string;
    name: string;
    contactPerson?: string;
    phone?: string;
    overdueAmount?: string;
    invoiceCount?: number;
    oldestInvoiceDays?: number;
  }) => Promise<void>;
  startBatchCalls: (
    customers: Array<{
      id: string;
      name: string;
      contactPerson?: string;
      phone?: string;
      overdueAmount?: string;
      invoiceCount?: number;
      oldestInvoiceDays?: number;
    }>,
  ) => Promise<void>;
  endCall: (customerId: string) => Promise<void>;
  endAllCalls: () => void;
  isAnyCallActive: boolean;
  clearCalls: () => void;
}

export function useARVapiCall(): UseARVapiCallReturn {
  const [customerCalls, setCustomerCalls] = useState<
    Map<string, CustomerCallState>
  >(new Map());
  const callIdsRef = useRef<Map<string, string>>(new Map());
  const eventSourcesRef = useRef<Map<string, EventSource>>(new Map());
  const messageIdCounterRef = useRef(0);

  // Generate unique message ID
  const generateMessageId = useCallback(() => {
    messageIdCounterRef.current += 1;
    return `ar-msg-${Date.now()}-${messageIdCounterRef.current}`;
  }, []);

  // Update a specific customer's call state
  const updateCustomerCallState = useCallback(
    (
      customerId: string,
      updates: Partial<Omit<CustomerCallState, 'customerId' | 'customerName'>>,
    ) => {
      setCustomerCalls((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(customerId);
        if (existing) {
          newMap.set(customerId, { ...existing, ...updates });
        }
        return newMap;
      });
    },
    [],
  );

  // Add a transcript message for a specific customer
  const addTranscriptMessage = useCallback(
    (
      customerId: string,
      message: Omit<TranscriptMessage, 'id' | 'timestamp'>,
    ) => {
      const text = message.text.trim();
      if (!text) return;

      setCustomerCalls((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(customerId);
        if (!existing) return prev;

        const updatedTranscript = [...existing.transcript];
        const lastIdx = updatedTranscript.length - 1;

        // Find which message to update (if any)
        let updateIdx = -1;

        if (lastIdx >= 0) {
          const lastMsg = updatedTranscript[lastIdx];

          if (lastMsg.role === message.role && !lastMsg.isFinal) {
            updateIdx = lastIdx;
          }
        }

        if (updateIdx >= 0) {
          const existingMsg = updatedTranscript[updateIdx];
          const existingTextLower = existingMsg.text.trim().toLowerCase();
          const newTextLower = text.toLowerCase();

          // Skip exact duplicates
          if (existingTextLower === newTextLower) {
            if (message.isFinal && !existingMsg.isFinal) {
              updatedTranscript[updateIdx] = { ...existingMsg, isFinal: true };
              newMap.set(customerId, {
                ...existing,
                transcript: updatedTranscript,
              });
              return newMap;
            }
            return prev;
          }

          // Replace with new text
          updatedTranscript[updateIdx] = {
            ...existingMsg,
            text: text,
            isFinal: message.isFinal,
            timestamp: new Date(),
          };
        } else {
          // Add new entry
          updatedTranscript.push({
            ...message,
            text: text,
            id: generateMessageId(),
            timestamp: new Date(),
          });
        }

        newMap.set(customerId, {
          ...existing,
          transcript: updatedTranscript,
        });
        return newMap;
      });
    },
    [generateMessageId],
  );

  // Mark all messages as final
  const finalizeTranscript = useCallback((customerId: string) => {
    setCustomerCalls((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(customerId);
      if (existing) {
        const finalizedTranscript = existing.transcript.map((msg) => ({
          ...msg,
          isFinal: true,
        }));
        newMap.set(customerId, {
          ...existing,
          transcript: finalizedTranscript,
        });
      }
      return newMap;
    });
  }, []);

  // Connect to SSE stream for live transcript updates
  const connectToStream = useCallback(
    (customerId: string, callId: string) => {
      const eventSource = new EventSource(`${API_BASE}/call/${callId}/stream`, {
        withCredentials: true,
      });

      eventSourcesRef.current.set(customerId, eventSource);

      eventSource.addEventListener('init', (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📊 AR Call init:', data);
        } catch (error) {
          console.error('Error parsing init event:', error);
        }
      });

      eventSource.addEventListener('status', (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📊 AR Status update:', data.status);

          if (data.status === 'queued' || data.status === 'connecting') {
            updateCustomerCallState(customerId, { status: 'connecting' });
          } else if (data.status === 'ringing') {
            updateCustomerCallState(customerId, { status: 'ringing' });
          } else if (
            data.status === 'in-progress' ||
            data.status === 'active'
          ) {
            updateCustomerCallState(customerId, { status: 'active' });
          } else if (data.status === 'ended' || data.status === 'completed') {
            updateCustomerCallState(customerId, { status: 'ended' });
          }
        } catch (error) {
          console.error('Error parsing status event:', error);
        }
      });

      eventSource.addEventListener('transcript', (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📝 AR Transcript:', data.role, data.text);

          addTranscriptMessage(customerId, {
            role: data.role as 'assistant' | 'user',
            text: data.text,
            isFinal: data.isFinal !== false,
          });
        } catch (error) {
          console.error('Error parsing transcript event:', error);
        }
      });

      eventSource.addEventListener('ended', (event) => {
        console.log('📞 AR Call ended:', event.data);
        finalizeTranscript(customerId);
        updateCustomerCallState(customerId, { status: 'ended' });
        eventSource.close();
        eventSourcesRef.current.delete(customerId);
      });

      eventSource.onerror = () => {
        console.log('AR SSE connection error/closed');
      };
    },
    [updateCustomerCallState, addTranscriptMessage, finalizeTranscript],
  );

  // Start a single call
  const startCall = useCallback(
    async (customer: {
      id: string;
      name: string;
      contactPerson?: string;
      phone?: string;
      overdueAmount?: string;
      invoiceCount?: number;
      oldestInvoiceDays?: number;
    }) => {
      // Initialize call state
      setCustomerCalls((prev) => {
        const newMap = new Map(prev);
        newMap.set(customer.id, {
          customerId: customer.id,
          customerName: customer.name,
          status: 'connecting',
          transcript: [],
        });
        return newMap;
      });

      try {
        const response = await fetch(`${API_BASE}/call/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            customerId: customer.id,
            customerName: customer.name,
            contactPerson: customer.contactPerson,
            phone: customer.phone,
            overdueAmount: customer.overdueAmount,
            invoiceCount: customer.invoiceCount,
            oldestInvoiceDays: customer.oldestInvoiceDays,
          }),
        });

        const responseText = await response.text();

        if (!response.ok) {
          let errorMessage = `Server error: ${response.status}`;
          try {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.error || errorMessage;
          } catch {
            errorMessage = responseText || errorMessage;
          }
          throw new Error(errorMessage);
        }

        const data = JSON.parse(responseText);
        const callId = data.callId;

        console.log('📞 AR Call started:', callId);
        callIdsRef.current.set(customer.id, callId);

        updateCustomerCallState(customer.id, {
          status: 'ringing',
          callId,
        });

        // Add initial message
        addTranscriptMessage(customer.id, {
          role: 'assistant',
          text: `📞 Calling ${customer.name}...`,
          isFinal: true,
        });

        // Connect to SSE stream
        connectToStream(customer.id, callId);
      } catch (error) {
        console.error('Failed to start AR call:', error);
        updateCustomerCallState(customer.id, {
          status: 'error',
          error:
            error instanceof Error ? error.message : 'Failed to start call',
        });
      }
    },
    [updateCustomerCallState, addTranscriptMessage, connectToStream],
  );

  // Start batch calls (for "resolve all issues")
  const startBatchCalls = useCallback(
    async (
      customers: Array<{
        id: string;
        name: string;
        contactPerson?: string;
        phone?: string;
        overdueAmount?: string;
        invoiceCount?: number;
        oldestInvoiceDays?: number;
      }>,
    ) => {
      // For demo: we're calling Angel and Noam, not the actual customers
      // Initialize call states for demo targets
      const demoTargets = ['angel', 'noam'];
      setCustomerCalls((prev) => {
        const newMap = new Map(prev);
        newMap.set('angel', {
          customerId: 'angel',
          customerName: 'Angel',
          status: 'connecting',
          transcript: [],
        });
        newMap.set('noam', {
          customerId: 'noam',
          customerName: 'Noam',
          status: 'connecting',
          transcript: [],
        });
        return newMap;
      });

      try {
        const response = await fetch(`${API_BASE}/calls/start-batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ customers }),
        });

        const responseText = await response.text();

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const data = JSON.parse(responseText);
        console.log('📞 AR Batch calls started:', data);

        // Process results - now using targetName (Angel/Noam) as the ID
        for (const result of data.calls) {
          const targetId =
            result.targetName?.toLowerCase() || result.customerId;
          const targetName = result.targetName || result.customerName;

          if (result.status === 'started' && result.callId) {
            callIdsRef.current.set(targetId, result.callId);

            updateCustomerCallState(targetId, {
              status: 'ringing',
              callId: result.callId,
            });

            addTranscriptMessage(targetId, {
              role: 'assistant',
              text: `📞 Calling ${targetName}...`,
              isFinal: true,
            });

            // Connect to SSE stream
            connectToStream(targetId, result.callId);
          } else {
            updateCustomerCallState(targetId, {
              status: 'error',
              error: result.error || 'Failed to start call',
            });
          }
        }
      } catch (error) {
        console.error('Failed to start batch AR calls:', error);
        // Mark all as error
        for (const targetId of demoTargets) {
          updateCustomerCallState(targetId, {
            status: 'error',
            error:
              error instanceof Error ? error.message : 'Failed to start calls',
          });
        }
      }
    },
    [updateCustomerCallState, addTranscriptMessage, connectToStream],
  );

  // End a specific call
  const endCall = useCallback(
    async (customerId: string) => {
      const callId = callIdsRef.current.get(customerId);

      // Close EventSource
      const eventSource = eventSourcesRef.current.get(customerId);
      if (eventSource) {
        eventSource.close();
        eventSourcesRef.current.delete(customerId);
      }

      // End call via backend
      if (callId) {
        try {
          await fetch(`${API_BASE}/call/${callId}/end`, {
            method: 'POST',
            credentials: 'include',
          });
        } catch (error) {
          console.error('Error ending AR call:', error);
        }
        callIdsRef.current.delete(customerId);
      }

      finalizeTranscript(customerId);
      updateCustomerCallState(customerId, { status: 'ended' });
    },
    [updateCustomerCallState, finalizeTranscript],
  );

  // End all active calls
  const endAllCalls = useCallback(() => {
    for (const customerId of callIdsRef.current.keys()) {
      endCall(customerId);
    }
  }, [endCall]);

  // Clear all call state
  const clearCalls = useCallback(() => {
    // End all active calls first
    for (const eventSource of eventSourcesRef.current.values()) {
      eventSource.close();
    }
    eventSourcesRef.current.clear();

    for (const callId of callIdsRef.current.values()) {
      fetch(`${API_BASE}/call/${callId}/end`, {
        method: 'POST',
        credentials: 'include',
      }).catch(console.error);
    }
    callIdsRef.current.clear();

    setCustomerCalls(new Map());
  }, []);

  // Check if any call is active
  const isAnyCallActive = Array.from(customerCalls.values()).some(
    (call) =>
      call.status === 'active' ||
      call.status === 'connecting' ||
      call.status === 'ringing',
  );

  // Cleanup on unmount
  useEffect(() => {
    const eventSources = eventSourcesRef.current;
    const callIds = callIdsRef.current;

    return () => {
      for (const eventSource of eventSources.values()) {
        eventSource.close();
      }
      eventSources.clear();

      for (const callId of callIds.values()) {
        fetch(`${API_BASE}/call/${callId}/end`, {
          method: 'POST',
          credentials: 'include',
        }).catch(console.error);
      }
      callIds.clear();
    };
  }, []);

  return {
    customerCalls,
    startCall,
    startBatchCalls,
    endCall,
    endAllCalls,
    isAnyCallActive,
    clearCalls,
  };
}
